import type { InboxPullRequest } from '@/app/(home)/inbox/types';
import type { QueryClient } from '@tanstack/react-query';

import type { InboxV2SectionResponse } from './use-inbox-v2-pull-requests';

import { setPendingOptimisticMutation } from './pending-optimistic-mutations';
import {
  inboxV2MergedPullRequestsQueryKeyPrefix,
  inboxV2OpenPullRequestsQueryKeyPrefix,
} from './query-keys';

export type PullRequestIdentity = {
  owner: string;
  repo: string;
  number: number;
};

export function isSamePullRequest(pr: InboxPullRequest, identity: PullRequestIdentity): boolean {
  return (
    pr.repository.owner.toLowerCase() === identity.owner.toLowerCase() &&
    pr.repository.name.toLowerCase() === identity.repo.toLowerCase() &&
    pr.number === identity.number
  );
}

/**
 * Patches a single PR across all inbox v2 query caches (open + merged).
 * Uses `setQueriesData` so every cache entry matching the prefix is updated regardless of the
 * installation/repository filter that produced it. This is a no-op on entries that don't contain
 * the target PR.
 */
export function patchInboxPullRequest(
  queryClient: QueryClient,
  identity: PullRequestIdentity,
  patch: (pr: InboxPullRequest) => InboxPullRequest,
) {
  const patchList = (list: InboxPullRequest[]) => {
    let changed = false;
    const next = list.map((pr) => {
      if (isSamePullRequest(pr, identity)) {
        changed = true;
        const patched = patch(pr);
        setPendingOptimisticMutation(identity, patched);
        return patched;
      }
      return pr;
    });
    return changed ? next : list;
  };

  for (const prefix of [
    inboxV2OpenPullRequestsQueryKeyPrefix(),
    inboxV2MergedPullRequestsQueryKeyPrefix(),
  ]) {
    queryClient.cancelQueries({ queryKey: prefix });
    queryClient.setQueriesData<InboxV2SectionResponse>({ queryKey: prefix }, (old) => {
      if (!old) {
        return old;
      }
      const nextOpen = patchList(old.openPullRequests);
      const nextMerged = patchList(old.mergedPullRequests);
      if (nextOpen === old.openPullRequests && nextMerged === old.mergedPullRequests) {
        return old;
      }
      return { ...old, openPullRequests: nextOpen, mergedPullRequests: nextMerged };
    });
  }
}
